// src/components/organisms/ChallengeDetailHeader.js

import React from "react";
import { Grid, Box, Button, Typography, Avatar, Link } from "@mui/material";
import PropTypes from "prop-types"; // Import PropTypes for type checking
import uploadCoverImage from "../../assets/upload-a-cover.webp";

const ChallengeDetailHeader = ({
  challenge,
  challengeId,
  user,
  navigate,
  theme,
  handleCreateSubmission,
  isCreating,
  hasEnded,
}) => {
  // Determine if the user is the creator
  const isCreator =
    user &&
    challenge.creatorInfo &&
    user.uid === challenge.creatorInfo.creatorUid;

  // Handler for button click
  const handleButtonClick = () => {
    if (isCreator) {
      navigate(`/challenge/edit/${challengeId}`); // Navigate to edit page if user is the creator
    } else if (hasEnded) {
      navigate(`/challenge/submissions/all/${challengeId}`); // Navigate to submissions page if challenge has ended
    } else {
      handleCreateSubmission(); // Take the challenge if the user is not the creator and challenge is ongoing
    }
  };

  // Determine button text
  let buttonText = "Take the Challenge";
  if (isCreator) {
    buttonText = "Edit this Challenge";
  } else if (hasEnded) {
    buttonText = "Challenge Ended. View Submissions";
  }

  return (
    <Box
      sx={{
        backgroundColor: "rgba(30, 65, 255, 0.25)",
        borderRadius: "50px",
        padding: 3,
        mb: 4,
      }}
    >
      <Grid container spacing={2} alignItems="stretch">
        {/* Column 1: Image */}
        <Grid item xs={12} md={2}>
          <Box
            sx={{
              width: "100%",
              paddingTop: "138.53%", // This maintains the 475:658 aspect ratio
              position: "relative", // Relative position for centered image
              overflow: "hidden", // Prevent overflow
            }}
          >
            <img
              src={challenge.hero || uploadCoverImage} // Fallback image
              alt="Challenge Cover"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)", // Center the image
                width: "100%",
                height: "100%",
                objectFit: "cover", // Maintain aspect ratio while covering the container
                borderRadius: "35px",
              }}
            />
          </Box>
        </Grid>

        {/* Column 2: Title, Payout, and Button */}
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between", // Distribute space between elements
            height: "100%", // Ensure full height for proper spacing
          }}
        >
          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              color: "white",
              fontFamily: "basic-sans",
              fontSize: "1.5rem",
            }}
          >
            {challenge.title}
            {isCreator && (
              <span> {challenge.published ? "(Live!)" : "(in draft)"}</span>
            )}
          </Typography>

          {/* Winner's Prize */}
          <Typography
            variant="h2"
            sx={{
              color: "white",
              fontFamily: "basic-sans",
              fontSize: "1.3rem",
              fontWeight: "700",
              my: 2, // Adds margin above and below the payout
              textAlign: "left", // Center the text horizontally
            }}
          >
            Winner's Prize: {challenge.prize}
          </Typography>

          {/* Conditionally Render the Button */}
          {(handleCreateSubmission || hasEnded) && (
            <Button
              onClick={handleButtonClick}
              variant="contained"
              disabled={isCreating}
              sx={{
                backgroundColor: isCreator
                  ? theme.colors.green
                  : hasEnded
                  ? theme.colors.red
                  : theme.colors.green,
                color: "white",
                padding: "10px 35px", // Adjust padding
                fontFamily: "basic-sans",
                fontWeight: "bold",
                fontSize: "1.3em",
                textTransform: "none", // No text transformation (e.g., uppercase)
                borderRadius: "50px", // Rounded button
                boxShadow: "none",
                alignSelf: "start", // Align button to the start (left)
                "&:hover": {
                  backgroundColor: isCreator
                    ? theme.colors.blue
                    : hasEnded
                    ? theme.colors.red // Assuming you have darkRed in your theme
                    : theme.colors.blue,
                  boxShadow: "none",
                  // backgroundColor: theme.colors.blue,
                },
              }}
            >
              {buttonText}
            </Button>
          )}
        </Grid>

        {/* Column 3: Creator Info */}
        <Grid item xs={12} md={3}>
          <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center", 
                height: "100%",
            }}
          >
            {/* Avatar */}
            <Avatar
              src={challenge.creatorInfo.profileImage}
              alt={challenge.creatorInfo.userName}
              sx={{ width: 50, height: 50 }}
            />

            {/* UserName as a Link */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start", // Corrected from "top" to "flex-start"
              }}
            >
              <Link
                href={`/challenge/profile/${challenge.creatorInfo.creatorUid}`}
                underline="none"
                sx={{
                  fontFamily: "basic-sans",
                  fontSize: "1.2em",
                  fontWeight: "500",
                  color: theme.colors.amber,
                  cursor: "pointer",
                  marginTop: "15px",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {challenge.creatorInfo.userName}
              </Link>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// Define PropTypes for better type checking
ChallengeDetailHeader.propTypes = {
  challenge: PropTypes.object.isRequired,
  challengeId: PropTypes.string.isRequired,
  user: PropTypes.object,
  navigate: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  handleCreateSubmission: PropTypes.func,
  isCreating: PropTypes.bool, // Corrected from PropTypes.func to PropTypes.bool
  hasEnded: PropTypes.bool, // Corrected from PropTypes.func to PropTypes.bool
};

export default ChallengeDetailHeader;
